/**
 * Count: place value you can pick up.
 *
 * Ones are little squares, a ten is a rod with ten notches, a hundred is a
 * flat hundred-square. Ten of anything can be pushed together into one of the
 * next thing, and any piece can be smashed back into ten of the thing below —
 * which is carrying and borrowing, met years before either word.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw } from 'lucide-preact';
import type { Vec } from '../../engine/vec.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import type { Counts } from '../../lib/placevalue.ts';
import { add, breakOne, canBreak, canGroup, countsOf, group, isTidy, PIECE_NAMES, TOP_PLACE, valueOf } from '../../lib/placevalue.ts';
import { groupDigits } from '../../lib/bignum.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

interface Hit {
  place: number;
  index: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Which places get a column: everything up to the biggest one in use. */
function placesShown(counts: Counts): number[] {
  let top = 2;
  for (let place = 0; place <= TOP_PLACE; place++) {
    if (counts[place] > 0) top = Math.max(top, place);
  }
  return Array.from({ length: top + 1 }, (_, i) => top - i);
}

const PLACE_COLORS = ['--crayon-teal', '--crayon-sky', '--crayon-purple', '--crayon-orange', '--crayon-red', '--crayon-green', '--crayon-yellow'];

export function PlaceValueToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const hitsRef = useRef<Hit[]>([]);
  const clockRef = useRef(0);

  const [counts, setCounts] = useState<Counts>(() => countsOf(24));
  const live = useRef(counts);
  live.current = counts;

  const total = valueOf(counts);
  const tidy = isTidy(counts);
  const groupable = counts.findIndex((_, place) => canGroup(counts, place));

  const burst = (at: Vec, many: number) => {
    spawnSparks(sparksRef.current, at, partyColors(), many, 320);
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const current = live.current;
      const places = placesShown(current);

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // The topbar and the readout pill float over this canvas, and the coach
      // sits along the bottom of it, so the chart lives strictly between them.
      const headerY = 150;
      const top = headerY + 56;
      const bottom = height - 74;
      const colW = width / places.length;
      const hits: Hit[] = [];

      places.forEach((place, column) => {
        const cx = colW * (column + 0.5);
        const many = current[place];
        const color = token(PLACE_COLORS[place] ?? '--crayon-teal');

        // Column name and the count under it, so the chart reads like a chart.
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillStyle = token('--ink-soft');
        ctx.font = `600 ${Math.min(15, colW * 0.16)}px ui-rounded, system-ui, sans-serif`;
        ctx.fillText(PIECE_NAMES[place] + (many === 1 ? '' : 's'), cx, headerY);
        ctx.font = `800 ${Math.min(30, colW * 0.3)}px ui-rounded, system-ui, sans-serif`;
        ctx.fillStyle = many >= 10 ? token('--crayon-green') : token('--ink');
        ctx.fillText(String(many), cx, headerY + 32);
        ctx.restore();

        if (many === 0) return;

        // Pieces stack up the column; ten or more get a nudge to be grouped.
        const size = Math.min(colW * 0.62, (bottom - top) / Math.max(4, Math.min(many, 10)) - 6);
        const pieceH = size * 0.55;
        const perRow = many > 10 ? 2 : 1;
        const rows = Math.ceil(many / perRow);
        const stackH = rows * (pieceH + 6);
        // Centred rather than sitting on the floor: with three pieces in a
        // column, floor-aligned stacks leave a hole under the headings.
        const startY = Math.max(top, top + (bottom - top - stackH) / 2);

        for (let i = 0; i < many; i++) {
          const row = Math.floor(i / perRow);
          const col = i % perRow;
          const w = perRow === 1 ? size : size * 0.46;
          const x = cx - (perRow === 1 ? w / 2 : (col === 0 ? w + 3 : -3));
          const glow = many >= 10 ? Math.sin(clockRef.current * 4 + i * 0.4) * 0.5 + 0.5 : 0;
          const y = startY + row * (pieceH + 6) - glow * 2;
          drawPiece(ctx, place, x, y, w, pieceH, color, glow);
          hits.push({ place, index: i, x, y, w, h: pieceH });
        }
      });

      hitsRef.current = hits;
      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      // Tapping a piece smashes it into ten of the next one down.
      const hit = hitsRef.current.find(
        (h) => p.pos.x >= h.x && p.pos.x <= h.x + h.w && p.pos.y >= h.y && p.pos.y <= h.y + h.h
      );
      if (!hit || !canBreak(live.current, hit.place)) return;
      setCounts(breakOne(live.current, hit.place));
      burst({ x: hit.x + hit.w / 2, y: hit.y + hit.h / 2 }, 14);
      play('pop', 0.6);
    },
  });

  const groupNow = () => {
    if (groupable < 0) return;
    setCounts(group(counts, groupable));
    play('yay', 0.7);
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      burst({ x: rect.width / 2, y: rect.height * 0.45 }, 22);
    }
  };

  const reset = () => {
    setCounts(countsOf(24));
    play('whoosh', 0.6);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Place value blocks" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton round label="Start again" hideLabel onClick={reset}>
            <RotateCcw size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>

        <p class="readout">
          <strong>{groupDigits(BigInt(total))}</strong>
          <span>{tidy ? 'all tidied up' : 'ten of something can be pushed together'}</span>
        </p>

        <Coach>{groupable >= 0 ? 'Ten of those make one bigger one' : 'Tap a piece to smash it into ten'}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="Add a piece">
          {PIECE_NAMES.slice(0, 5).map((name, place) => (
            <Chip
              key={name}
              label={`+ ${name}`}
              accent={token(PLACE_COLORS[place])}
              tinted
              onClick={() => {
                setCounts(add(live.current, place));
                play('count', Math.min(1, (place + 2) / 6));
              }}
            />
          ))}
        </div>
        {/* The main action gets its own row. Squeezed in beside the chips its
            label ran off the side of the screen — there is room again now that
            the tab strip is two rows instead of three. */}
        <div class="dock__row">
          <ToyButton
            wide
            label={groupable >= 0 ? `Push ten ${PIECE_NAMES[groupable]}s together` : 'Nothing to push together'}
            accent={token('--crayon-green')}
            disabled={groupable < 0}
            onClick={groupNow}
          />
        </div>
      </div>
    </div>
  );
}

function drawPiece(
  ctx: CanvasRenderingContext2D,
  place: number,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  glow: number
): void {
  ctx.save();
  if (glow > 0) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 6 + glow * 10;
  }
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, Math.min(8, h * 0.3));
  ctx.fill();
  ctx.stroke();
  ctx.shadowBlur = 0;

  // A ten is notched into ten, a hundred is ruled into a hundred square; past
  // that there is nothing legible to draw, so the piece says what it is worth.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 1.5;
  if (place === 1) {
    for (let i = 1; i < 10; i++) {
      const gx = x + (w / 10) * i;
      ctx.beginPath();
      ctx.moveTo(gx, y + 2);
      ctx.lineTo(gx, y + h - 2);
      ctx.stroke();
    }
  } else if (place === 2) {
    for (let i = 1; i < 10; i++) {
      const gx = x + (w / 10) * i;
      const gy = y + (h / 10) * i;
      ctx.beginPath();
      ctx.moveTo(gx, y + 1);
      ctx.lineTo(gx, y + h - 1);
      ctx.moveTo(x + 1, gy);
      ctx.lineTo(x + w - 1, gy);
      ctx.stroke();
    }
  } else if (place > 2) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 ${Math.min(16, h * 0.5)}px ui-rounded, system-ui, sans-serif`;
    ctx.fillText(groupDigits(BigInt(10 ** place)), x + w / 2, y + h / 2);
  }
  ctx.restore();
}
